import * as React from "react";
import { Button, Checkbox } from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { IRemit } from "../../../types/remits.types";
import AddRemitInsurancePaymentForm from "../../AddRemitInsurancePaymentForm/AddRemitInsurancePaymentForm";
import InsurancePaymentItem from "../InsurancePaymentItem/InsurancePaymentItem";
import PlusIcon from "../../../../../icons/Plus";
import {
    getAdjustmentCodeTotals,
    getPaymentRemarkCodeTotals,
} from "../../../helpers/helpers.remits";

const PAYMENTS_GRID_COLS =
    "grid-cols-[20px_150px_150px_150px_150px_300px_100px_120px_260px_150px_100px_150px_200px]";

interface BatchInsurancePaymentsProps {
    remits: IRemit[];
}

export default function BatchInsurancePayments({
    remits,
}: BatchInsurancePaymentsProps) {
    const [isAddingRow, setIsAddingRow] = React.useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const paymentsArray = searchParams.getAll("selectedPayments");
    const allPayments = remits.flatMap((r) => r.insurance_payments || []);
    const eligiblePayments = allPayments.filter(
        (p) => p.payment_status !== "completed"
    );
    const isAllChecked =
        eligiblePayments.length > 0 &&
        eligiblePayments.every((p) => paymentsArray.includes(p.payment_id));
    const primaryRemit = remits[0];
    const remarkCodeTotalsByRemit = new Map(
        remits.map((remit) => [
            remit.remittance_id,
            {
                payment: getPaymentRemarkCodeTotals(
                    remit.insurance_payments ?? []
                ),
                adjustment: getAdjustmentCodeTotals(
                    remit.claim_adjustments ?? []
                ),
            },
        ])
    );

    const handleBulkCheck = (checked: boolean) => {
        const otherSelections = paymentsArray.filter(
            (id) => !allPayments.some((p) => p.payment_id === id)
        );

        searchParams.delete("selectedPayments");
        otherSelections.forEach((id) => {
            searchParams.append("selectedPayments", id);
        });

        if (checked) {
            eligiblePayments.forEach((payment) => {
                searchParams.append("selectedPayments", payment.payment_id);
            });
        }

        setSearchParams(searchParams);
    };

    return (
        <div className="rounded-md [border:1px_solid_var(--color-stroke)] border border-strokedark w-full !overflow-x-auto">
            <div className="table w-full">
                <div
                    className={`p-12 [border-bottom:1px_solid_var(--color-stroke)] border-b border-b-strokedark grid gap-x-3 ${PAYMENTS_GRID_COLS} justify-stretch text-left text-xs uppercase font-semibold text-textSec items-center`}
                >
                    {eligiblePayments.length > 0 ? (
                        <Checkbox
                            name=""
                            value=""
                            handleChange={(e) =>
                                handleBulkCheck(e.target.checked)
                            }
                            isChecked={isAllChecked}
                        />
                    ) : (
                        <span />
                    )}
                    <span className="w-[150px]" />
                    <span>payment date</span>
                    <span>claim received date</span>
                    <span>payment source</span>
                    <span>payment type</span>
                    <span>amount</span>
                    <span>payment status</span>
                    <span>remark codes</span>
                    <span>eft</span>
                    <span>date posted</span>
                    <span>posted by</span>
                    <span>payment notes</span>
                </div>

                {remits.map((remit) => {
                    const cpt = remit.remittance_cpt_code || remit.cpt_code;
                    const payments = remit.insurance_payments || [];
                    const remarkCodeTotals = remarkCodeTotalsByRemit.get(
                        remit.remittance_id
                    );
                    return (
                        <React.Fragment key={`pay-cpt-${remit.remittance_id}`}>
                            <div className="px-12 py-8 bg-[--card-bg-gray] [border-bottom:1px_solid_var(--color-stroke)] text-xs font-semibold uppercase tracking-wide text-textSec">
                                CPT {cpt || "--"}
                            </div>
                            {payments.length === 0 ? (
                                <div className="px-12 py-8 [border-bottom:1px_solid_var(--color-stroke)] text-sm text-textSec">
                                    No payments yet for this CPT
                                </div>
                            ) : (
                                payments.map((payment) => (
                                    <InsurancePaymentItem
                                        key={payment.payment_id}
                                        payment={payment}
                                        invoiceId={remit.invoice_id}
                                        remitClaimAdjustments={
                                            remit.claim_adjustments
                                        }
                                        remittanceId={remit.remittance_id}
                                        claimReceivedDate={
                                            remit.remit_payment_date
                                        }
                                        remarkCodePaymentTotals={
                                            remarkCodeTotals?.payment
                                        }
                                        adjustmentCodeTotals={
                                            remarkCodeTotals?.adjustment
                                        }
                                    />
                                ))
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            <div className="p-12">
                {!isAddingRow ? (
                    <div className="flex justify-center">
                        <Button
                            size="auto"
                            variant="normal"
                            className="font-normal text-sm flex gap-x-3 items-center"
                            onClick={() => setIsAddingRow(true)}
                        >
                            <PlusIcon className="stroke-text" />
                            Add insurance payment
                        </Button>
                    </div>
                ) : (
                    <AddRemitInsurancePaymentForm
                        invoiceId={primaryRemit.invoice_id}
                        availableRemits={remits}
                        onCancelClick={() => setIsAddingRow(false)}
                        onFormSubmit={() => setIsAddingRow(false)}
                    />
                )}
            </div>
        </div>
    );
}
